import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLearningProgress, useSaveQuizAnswer } from "../store";
import BunnyVideo from "../video/BunnyVideo";

/**
 * Dependency-free renderer for Notion/Tiptap JSON documents.
 *
 * Walks the JSON schema produced by the editor (see `extensions.js`) and
 * renders plain React elements. No Tiptap/ProseMirror runtime is involved, so
 * this is safe to use anywhere (including non-editor bundles and SSR).
 *
 * Supported nodes: doc, paragraph, text, heading, bulletList, orderedList,
 * listItem, taskList, taskItem, blockquote, codeBlock, horizontalRule, image,
 * table, tableRow, tableHeader, tableCell, hardBreak, callout, accordion,
 * quiz, flashcards, embed, video.
 *
 * Supported marks: bold, italic, underline, strike, code, link, highlight,
 * subscript, superscript, textStyle/color.
 */

/* ------------------------------------------------------------------ *
 * Mark helpers
 * ------------------------------------------------------------------ */

const TAG_MARKS = {
  bold: "strong",
  italic: "em",
  underline: "u",
  strike: "s",
  code: "code",
  subscript: "sub",
  superscript: "sup",
};

function renderMark(mark, children) {
  switch (mark.type) {
    case "link": {
      const href = mark.attrs?.href;
      if (!href) return children;
      const target = mark.attrs?.target || null;
      return (
        <a
          href={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
        >
          {children}
        </a>
      );
    }
    case "textStyle":
    case "color": {
      const color = mark.attrs?.color;
      return color ? <span style={{ color }}>{children}</span> : children;
    }
    case "highlight": {
      const color = mark.attrs?.color;
      return (
        <mark style={color ? { backgroundColor: color } : undefined}>
          {children}
        </mark>
      );
    }
    default: {
      const tag = TAG_MARKS[mark.type];
      if (!tag) return children;
      return React.createElement(tag, null, children);
    }
  }
}

function renderText(node) {
  const text = node.text ?? "";
  if (!Array.isArray(node.marks) || node.marks.length === 0) return text;
  let out = text;
  node.marks.forEach((mark) => {
    out = renderMark(mark, out);
  });
  return out;
}

/* ------------------------------------------------------------------ *
 * Block helpers
 * ------------------------------------------------------------------ */

function alignStyle(node) {
  const align = node.attrs?.textAlign;
  return align ? { textAlign: align } : undefined;
}

function nodeText(node) {
  if (!node || typeof node !== "object") return "";
  if (node.type === "text") return node.text ?? "";
  if (Array.isArray(node.content)) return node.content.map(nodeText).join("");
  return "";
}

function cellProps(node) {
  const a = node.attrs || {};
  const props = {};
  if (a.colspan) props.colSpan = a.colspan;
  if (a.rowspan) props.rowSpan = a.rowspan;
  return props;
}

/* ------------------------------------------------------------------ *
 * Custom block components
 * ------------------------------------------------------------------ */

function CalloutBlock({ node }) {
  const emoji = node.attrs?.emoji || "💡";
  return (
    <div className="callout-block" data-callout>
      <span className="callout-emoji">{emoji}</span>
      <div className="callout-content">{renderInline(node.content)}</div>
    </div>
  );
}

function AccordionBlock({ node }) {
  const [open, setOpen] = useState(!!node.attrs?.open);
  const title = node.attrs?.title || "";

  return (
    <div className="accordion-block" data-accordion>
      <div className="accordion-header">
        <button
          type="button"
          className="accordion-toggle"
          onClick={() => setOpen((o) => !o)}
          title={open ? "Collapse" : "Expand"}
        >
          <span className={`accordion-chevron${open ? " is-open" : ""}`}>
            ›
          </span>
        </button>
        <span className="accordion-title">{title}</span>
      </div>
      {open && (
        <div className="accordion-content">{renderNodes(node.content)}</div>
      )}
    </div>
  );
}

function QuizTaker({ options, correct, multiple, question }) {
  const [selected, setSelected] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const lessonId = useContext(LessonIdContext);
  const saveQuizAnswer = useSaveQuizAnswer();
  const { progress } = useLearningProgress();

  // Restore the most recent stored answer for this question (if any).
  const stored = useMemo(() => {
    const answers = lessonId ? (progress[lessonId]?.quizAnswers ?? []) : [];
    return [...answers].reverse().find((a) => a.question === question);
  }, [lessonId, progress, question]);

  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !stored) return;
    const storedIndices = stored.selected
      .map((label) => options.indexOf(label))
      .filter((i) => i >= 0);
    if (storedIndices.length) {
      setSelected(storedIndices);
      setSubmitted(true);
      restoredRef.current = true;
    }
  }, [stored, options]);

  const isSelected = (index) => selected.includes(index);
  const isCorrectChoice = (index) => correct.includes(index);

  const toggle = (index) => {
    if (submitted) return;
    setSelected((prev) =>
      multiple
        ? prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
        : [index],
    );
  };

  const score = (() => {
    if (!submitted || correct.length === 0) return null;
    const chosen = new Set(selected);
    const right = new Set(correct);
    const pickedRight = [...chosen].filter((i) => right.has(i)).length;
    const pickedWrong = [...chosen].filter((i) => !right.has(i)).length;
    const correctPick = pickedRight === right.size && pickedWrong === 0;
    return { correctPick };
  })();

  const submitQuiz = () => {
    setSubmitted(true);
    if (lessonId && question) {
      saveQuizAnswer(lessonId, {
        lessonId,
        question,
        selected: selected.map((i) => options[i]),
        correct: score?.correctPick ?? false,
        answeredAt: new Date().toISOString(),
      });
    }
  };

  return (
    <div className="quiz-take">
      <div className="quiz-options" role="group" aria-label="Answer options">
        {options.map((option, index) => {
          const chosen = isSelected(index);
          const classes = [
            "quiz-option",
            "quiz-option-clickable",
            !submitted && chosen ? "is-chosen" : "",
            submitted && isCorrectChoice(index) ? "is-correct" : "",
            submitted && chosen && !isCorrectChoice(index) ? "is-wrong" : "",
          ]
            .filter(Boolean)
            .join(" ");

          // Only reveal right/wrong marks after submission.
          const checkMark = !submitted
            ? chosen
              ? "●"
              : ""
            : isCorrectChoice(index)
              ? "✓"
              : chosen
                ? "✕"
                : "";

          return (
            <button
              type="button"
              key={index}
              className={classes}
              onClick={() => toggle(index)}
              disabled={submitted}
            >
              <span className="quiz-option-check">{checkMark}</span>
              <span className="quiz-option-text">{option}</span>
            </button>
          );
        })}
      </div>

      <div className="quiz-take-footer">
        {!submitted ? (
          <button
            type="button"
            className="quiz-submit"
            disabled={selected.length === 0}
            onClick={submitQuiz}
          >
            Submit
          </button>
        ) : (
          <>
            <span
              className={`quiz-result${score?.correctPick ? " is-correct" : ""}`}
            >
              {score === null
                ? "No correct answer set"
                : score.correctPick
                  ? "Correct!"
                  : "Incorrect"}
            </span>
            <button
              type="button"
              className="quiz-submit"
              onClick={() => {
                setSelected([]);
                setSubmitted(false);
              }}
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function QuizBlock({ node }) {
  const options = node.attrs?.options || [];
  const correct = node.attrs?.correct || [];
  const multiple = !!node.attrs?.multiple;
  const question =
    nodeText(node) || (node.content ? renderInline(node.content) : "");

  return (
    <div className="quiz-block quiz-block-take" data-quiz>
      <div className="quiz-question">{renderInline(node.content)}</div>
      <QuizTaker
        options={options}
        correct={correct}
        multiple={multiple}
        question={question}
      />
    </div>
  );
}

function FlashcardsReview({ cards }) {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);

  if (!cards.length) {
    return <div className="flashcards-empty">No cards in this deck</div>;
  }

  const card = cards[Math.min(index, cards.length - 1)];

  const go = (delta) => {
    setFlipped(false);
    setIndex((i) => (i + delta + cards.length) % cards.length);
  };

  return (
    <div className="flashcards-review">
      <div
        className={`flashcard-review-card${flipped ? " is-flipped" : ""}`}
        onClick={() => setFlipped((f) => !f)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setFlipped((f) => !f);
        }}
      >
        <div className="flashcard-review-face">
          <span className="flashcard-review-label">Front</span>
          <div className="flashcard-review-text">
            {renderNodes(card.front?.content)}
          </div>
        </div>
        <div className="flashcard-review-face flashcard-review-face-back">
          <span className="flashcard-review-label">Back</span>
          <div className="flashcard-review-text">
            {renderNodes(card.back?.content)}
          </div>
        </div>
      </div>

      <div className="flashcards-nav">
        <button
          type="button"
          className="flashcard-nav-btn"
          onClick={() => go(-1)}
        >
          ‹ Prev
        </button>
        <span className="flashcard-nav-index">
          {index + 1} / {cards.length}
        </span>
        <button
          type="button"
          className="flashcard-nav-btn"
          onClick={() => go(1)}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}

function FlashcardsBlock({ node }) {
  const cards = node.attrs?.cards || [];

  return (
    <div className="flashcards-block flashcards-block-review" data-flashcards>
      <div className="flashcards-header">
        <span className="flashcards-label">Flashcards</span>
        <span className="flashcards-count">{cards.length} cards</span>
      </div>
      <FlashcardsReview cards={cards} />
    </div>
  );
}

function EmbedBlock({ node }) {
  const { src, provider } = node.attrs || {};
  return (
    <div className="embed-block" data-embed>
      <div className="embed-header">
        <span className="embed-label">{provider || "Embed"}</span>
      </div>
      {src ? (
        <div className="embed-frame">
          <iframe
            src={src}
            title={provider || "Embedded content"}
            loading="lazy"
            allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;fullscreen"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="embed-empty">No embed source</div>
      )}
    </div>
  );
}

function VideoBlock({ node }) {
  const { src, title, videoId, libraryId } = node.attrs || {};
  return (
    <div className="video-block" data-video>
      {videoId ? (
        <BunnyVideo libraryId={libraryId || "231224"} videoId={videoId} />
      ) : (
        <video controls src={src} title={title || undefined} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Node dispatcher
 * ------------------------------------------------------------------ */

function renderInline(content) {
  if (!Array.isArray(content)) return null;
  return content.map((node, i) => renderNode(node, `i${i}`));
}

function renderNodes(content) {
  if (!Array.isArray(content)) return null;
  return content.map((node, i) => renderNode(node, `b${i}`));
}

function renderNode(node, key) {
  if (!node || typeof node !== "object") return null;

  switch (node.type) {
    case "doc":
      return (
        <React.Fragment key={key}>{renderNodes(node.content)}</React.Fragment>
      );

    case "text":
      return renderText(node);

    case "hardBreak":
      return <br key={key} />;

    case "paragraph":
      return (
        <p key={key} style={alignStyle(node)}>
          {renderInline(node.content)}
        </p>
      );

    case "heading": {
      const level = Math.min(Math.max(node.attrs?.level || 1, 1), 6);
      return React.createElement(
        `h${level}`,
        { key, style: alignStyle(node) },
        renderInline(node.content),
      );
    }

    case "bulletList":
      return <ul key={key}>{renderNodes(node.content)}</ul>;

    case "orderedList": {
      const start = node.attrs?.start;
      return (
        <ol key={key} start={start && start !== 1 ? start : undefined}>
          {renderNodes(node.content)}
        </ol>
      );
    }

    case "listItem":
      return <li key={key}>{renderNodes(node.content)}</li>;

    case "taskList":
      return (
        <ul key={key} data-type="taskList">
          {renderNodes(node.content)}
        </ul>
      );

    case "taskItem":
      return (
        <li key={key} data-checked={node.attrs?.checked ? "true" : "false"}>
          <label>
            <input
              type="checkbox"
              checked={!!node.attrs?.checked}
              readOnly
              disabled
            />
          </label>
          <div>{renderNodes(node.content)}</div>
        </li>
      );

    case "blockquote":
      return <blockquote key={key}>{renderNodes(node.content)}</blockquote>;

    case "codeBlock": {
      const language = node.attrs?.language;
      return (
        <pre key={key}>
          <code className={language ? `language-${language}` : undefined}>
            {nodeText(node)}
          </code>
        </pre>
      );
    }

    case "horizontalRule":
      return <hr key={key} />;

    case "image":
      return (
        <img
          key={key}
          className="img-fluid"
          src={node.attrs?.src}
          alt={node.attrs?.alt || undefined}
          title={node.attrs?.title || undefined}
        />
      );

    case "table":
      return (
        <table key={key}>
          <tbody>{renderNodes(node.content)}</tbody>
        </table>
      );

    case "tableRow":
      return <tr key={key}>{renderNodes(node.content)}</tr>;

    case "tableHeader":
      return (
        <th key={key} {...cellProps(node)}>
          {renderNodes(node.content)}
        </th>
      );

    case "tableCell":
      return (
        <td key={key} {...cellProps(node)}>
          {renderNodes(node.content)}
        </td>
      );

    case "callout":
      return <CalloutBlock key={key} node={node} />;

    case "accordion":
      return <AccordionBlock key={key} node={node} />;

    case "quiz":
      return <QuizBlock key={key} node={node} />;

    case "flashcards":
      return <FlashcardsBlock key={key} node={node} />;

    case "embed":
      return <EmbedBlock key={key} node={node} />;

    case "video":
      return <VideoBlock key={key} node={node} />;

    default:
      // Unknown node: fall back to rendering any inline/block content it has.
      return node.content ? renderNodes(node.content) : null;
  }
}

/* ------------------------------------------------------------------ *
 * Lesson id context (passed down to quiz blocks so answers can be saved)
 * ------------------------------------------------------------------ */

const LessonIdContext = createContext(null);

/* ------------------------------------------------------------------ *
 * Public component
 * ------------------------------------------------------------------ */

function JsonNotionRenderer({ content = null, lessonId = "" }) {
  if (content == null) return null;

  // HTML string fallback (rendered verbatim; JSON is the preferred format).
  if (typeof content === "string") {
    return (
      <div
        className="notion-renderer-content"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  return (
    <LessonIdContext.Provider value={lessonId || null}>
      <div className="notion-renderer-content">
        {renderNodes(content.content)}
      </div>
    </LessonIdContext.Provider>
  );
}

export default JsonNotionRenderer;
